import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { type CompanyDetailType } from '@/api/activity';
import { type SolError } from '@/api/solType';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { Link } from '@/components/typography';
import { USDateViewFormat } from '@/constants/formats';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';

type Props = {
  data?: CompanyDetailType[];
  totalResults?: number;
  isLoading?: boolean;
  error: SolError | Error | null;
};

const AudienceMembershipListTable = ({ data, totalResults, isLoading = false, error }: Props) => {
  const sortFields = ROUTES.audienceDetail.searchParams['a.sortBy'].enumValues;

  const columns = useMemo<InstantSearchDataTableColumnDef<CompanyDetailType>[]>(
    () => [
      {
        header: t`Company Name`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: sortFields.includes('name'),
        minSize: 180,
        flex: 1,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.name;
          return field.tld && !!field.metrics?.visits && !!field.metrics.lastActivityDate ? (
            <Link
              variant="caption1"
              to={ROUTES.webTrafficActivityWithParams({ 'w.search': field.tld })}
            >
              {text}
            </Link>
          ) : (
            text
          );
        },
      },
      {
        header: t`Domain`,
        id: 'tld',
        minSize: 120,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.tld,
      },
      {
        header: t`Visits (Last 30d)`,
        id: 'metrics.visits',
        accessorKey: 'metrics.visits',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('metrics.visits'),
        minSize: 130,
        maxSize: 180,
        cell: ({ row }) => {
          const field = row.original;
          return field.metrics?.visits === 0 ? '' : numberFormat(field.metrics?.visits);
        },
      },
      {
        header: t`Last activity date`,
        id: 'metrics.lastActivityDate',
        accessorKey: 'metrics.lastActivityDate',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('metrics.lastActivityDate'),
        minSize: 140,
        maxSize: 180,
        cell: ({ row }) => {
          const field = row.original;
          return field.metrics?.lastActivityDate
            ? dayjs(field.metrics.lastActivityDate).format(USDateViewFormat)
            : '';
        },
      },
    ],
    [sortFields],
  );

  return (
    <InstantSearchDataTable<CompanyDetailType>
      columns={columns}
      data={data}
      totalResults={totalResults}
      loading={isLoading}
      emptyMessage={<Trans>No audience membership found.</Trans>}
      error={error as Error | null}
      getRowId={(record) => record.tld}
      initialColumnPinning={{
        start: ['name'],
      }}
    />
  );
};

export default AudienceMembershipListTable;
